import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BrandFormDialog } from "@/components/admin/BrandFormDialog";
import { SiteSettingsForm } from "@/components/admin/SiteSettingsForm";
import { Plus, Pencil, Eye, EyeOff, Trash2, ArrowUp, ArrowDown, LogOut, ExternalLink } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    api.get("/admin/brands").then((r) => setBrands(r.data));
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (brand) => {
    setEditing(brand);
    setDialogOpen(true);
  };

  const toggleVisible = async (brand) => {
    await api.put(`/admin/brands/${brand.id}`, { ...brand, is_visible: !brand.is_visible });
    toast.success(brand.is_visible ? "Brand hidden" : "Brand visible");
    load();
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= brands.length) return;
    const reordered = [...brands];
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    setBrands(reordered);
    await api.post("/admin/brands/reorder", { ordered_ids: reordered.map((b) => b.id) });
    load();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/admin/brands/${deleteTarget.id}`);
    toast.success("Brand deleted");
    setDeleteTarget(null);
    load();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-stone-100">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="font-heading text-lg font-semibold tracking-tight text-stone-900">The CASS Group Admin</h1>
            <p className="text-xs text-stone-400">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-stone-500 transition-colors hover:text-stone-900" data-testid="view-site-link">
              View site
            </a>
            <Button variant="outline" size="sm" className="rounded-lg" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <Tabs defaultValue="brands">
          <TabsList className="mb-6">
            <TabsTrigger value="brands" data-testid="tab-brands">Manage Brands</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Site Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="brands">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-stone-900">Brands</h2>
              <Button onClick={openAdd} className="rounded-lg" data-testid="add-brand-button">
                <Plus className="mr-2 h-4 w-4" /> Add New Brand
              </Button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-16">Order</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Website</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brands.map((brand, i) => (
                    <TableRow key={brand.id} data-testid={`brand-row-${brand.id}`}>
                      <TableCell>
                        <div className="flex flex-col">
                          <button onClick={() => move(i, -1)} disabled={i === 0} className="text-stone-400 hover:text-stone-900 disabled:opacity-30" data-testid={`move-up-${brand.id}`}>
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => move(i, 1)} disabled={i === brands.length - 1} className="text-stone-400 hover:text-stone-900 disabled:opacity-30" data-testid={`move-down-${brand.id}`}>
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone-100 bg-stone-50">
                            {brand.logo_url ? (
                              <img src={resolveUrl(brand.logo_url)} alt="" className="h-full w-full object-contain p-1 mix-blend-multiply" />
                            ) : (
                              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: brand.accent_color }} />
                            )}
                          </div>
                          <span className="font-medium text-stone-800">{brand.brand_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {brand.is_visible ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Visible</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-stone-500">Hidden</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-stone-500">
                        {brand.website_url ? (
                          <a href={brand.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-stone-900">
                            {brand.website_url.replace(/^https?:\/\/(www\.)?/, "")}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-stone-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(brand)} data-testid={`edit-brand-${brand.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => toggleVisible(brand)} data-testid={`toggle-brand-${brand.id}`}>
                            {brand.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(brand)} data-testid={`delete-brand-${brand.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {brands.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-10 text-center text-stone-400">No brands yet. Add your first brand.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="settings">
            {settings && <SiteSettingsForm settings={settings} onSaved={setSettings} />}
          </TabsContent>
        </Tabs>
      </main>

      {dialogOpen && (
        <BrandFormDialog
          key={editing?.id || "new"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          brand={editing}
          onSaved={load}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete brand?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{deleteTarget?.brand_name}</strong>? This cannot be undone. Consider hiding it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-button">
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
